import { useMemo, useState } from "react";

const pageSizes = [10, 25, 50, 100];

const textFromNode = (node) => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textFromNode).join(" ");
  if (node?.props?.children) return textFromNode(node.props.children);
  return "";
};

const escapeHtml = (value) => String(value)
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#039;");

const DataTable = ({ id, columns, rows, renderRow, emptyMessage = "Nenhum registo encontrado." }) => {
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const renderedRows = useMemo(() => {
    return rows.map((row, index) => {
      const element = renderRow(row, index);
      return {
        key: row?.id ?? row?.email ?? row?.nome ?? index,
        element,
        text: textFromNode(element).toLowerCase(),
      };
    });
  }, [rows, renderRow]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return renderedRows;
    return renderedRows.filter((row) => row.text.includes(term));
  }, [renderedRows, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const visibleRows = filteredRows.slice(startIndex, startIndex + pageSize);

  const exportText = () => {
    const header = columns.join("\t");
    const body = filteredRows.map((row) => row.text.replace(/\s+/g, " ").trim()).join("\n");
    return `${header}\n${body}`;
  };

  const copyRows = async () => {
    await navigator.clipboard?.writeText(exportText());
  };

  const printRows = () => {
    const popup = window.open("", "_blank", "width=960,height=720");
    if (!popup) return;

    popup.document.write(`
      <html>
        <head>
          <title>${id}</title>
          <style>
            body{font-family:Arial,sans-serif;padding:24px;color:#111}
            table{width:100%;border-collapse:collapse}
            th,td{border:1px solid #d0d5dd;padding:8px;text-align:left}
            th{background:#f3f4f6}
          </style>
        </head>
        <body>
          <table>
            <thead><tr>${columns.map((column) => `<th>${escapeHtml(column)}</th>`).join("")}</tr></thead>
            <tbody>${filteredRows.map((row) => `<tr><td colspan="${columns.length}">${escapeHtml(row.text)}</td></tr>`).join("")}</tbody>
          </table>
        </body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  const goToPage = (nextPage) => {
    setPage(Math.min(Math.max(1, nextPage), totalPages));
  };

  return (
    <div className="app-table-wrap">
      <div className="row align-items-center mb-2 g-2">
        <div className="col-md-6">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button type="button" className="btn btn-light btn-sm" onClick={copyRows}>Copiar</button>
            <button type="button" className="btn btn-light btn-sm" onClick={printRows}>Imprimir</button>
            <select
              className="form-select form-select-sm"
              style={{ width: 92 }}
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {pageSizes.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </div>
        </div>
        <div className="col-md-6">
          <input
            className="form-control form-control-sm"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Pesquisar..."
          />
        </div>
      </div>

      <div className="table-responsive">
        <table id={id} className="table table-centered table-hover table-bordered thead-dark align-middle mb-0 app-data-table">
          <thead>
            <tr>
              {columns.map((column) => <th key={column}>{column}</th>)}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length ? visibleRows.map((row) => row.element) : (
              <tr>
                <td colSpan={columns.length} className="text-center text-muted py-4">{emptyMessage}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="row align-items-center mt-2 g-2">
        <div className="col-md-6 text-muted small">
          {filteredRows.length ? `A mostrar ${startIndex + 1} a ${Math.min(startIndex + pageSize, filteredRows.length)} de ${filteredRows.length} registos` : "Sem registos"}
        </div>
        <div className="col-md-6">
          <div className="d-flex justify-content-md-end gap-1">
            <button type="button" className="btn btn-light btn-sm" disabled={currentPage === 1} onClick={() => goToPage(1)}>Primeiro</button>
            <button type="button" className="btn btn-light btn-sm" disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>Anterior</button>
            <span className="btn btn-light btn-sm disabled">{currentPage} / {totalPages}</span>
            <button type="button" className="btn btn-light btn-sm" disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>Seguinte</button>
            <button type="button" className="btn btn-light btn-sm" disabled={currentPage === totalPages} onClick={() => goToPage(totalPages)}>Ultimo</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
